/**
 * Superadmin CRUD over Person custom field definitions.
 *
 * Reads GET /api/person-fields with the current user id. That endpoint is
 * superadmin-only, so a 403 doubles as the "am I the server admin" signal: we
 * expose `isServerAdmin=false` and callers hide the panel. Any other error is
 * surfaced via `error`. Mirrors use_super_admins.js.
 *
 *   const { fields, isServerAdmin, loading, error, create, update, remove } = usePersonFieldAdmin();
 */

import { useState, useEffect, useCallback } from 'react';
import { useApi } from './use_api';
import { useAuth } from '../context/AuthContext';
import { invalidatePersonFieldsCache } from './use_person_fields';

export function usePersonFieldAdmin() {
    const api = useApi();
    const { user } = useAuth();
    const userId = user?.id;

    const [fields, setFields] = useState([]);
    const [isServerAdmin, setIsServerAdmin] = useState(false);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // `silent` refreshes after a mutation without flipping `loading`, so the
    // table doesn't dim + spin on every click; only the first load shows it.
    const load = useCallback(({ silent = false } = {}) => {
        if (!userId) { setLoading(false); return Promise.resolve(); }
        if (!silent) setLoading(true);
        return api.get('/api/person-fields', { params: { user_id: userId } })
            .then(data => {
                setFields(Array.isArray(data) ? data : []);
                setIsServerAdmin(true);
                setError(null);
            })
            .catch(err => {
                if (err.status === 403) {
                    setIsServerAdmin(false); // not the server admin — panel stays hidden
                } else {
                    setError(err);
                }
            })
            .finally(() => setLoading(false));
    }, [api, userId]);

    useEffect(() => { load(); }, [load]);

    const create = useCallback(payload => {
        return api.post('/api/person-fields', payload, { params: { user_id: userId } })
            .then(row => { invalidatePersonFieldsCache(); return load({ silent: true }).then(() => row); });
    }, [api, userId, load]);

    const update = useCallback((fieldId, payload) => {
        return api.put(`/api/person-fields/${fieldId}`, payload, { params: { user_id: userId } })
            .then(row => { invalidatePersonFieldsCache(); return load({ silent: true }).then(() => row); });
    }, [api, userId, load]);

    const remove = useCallback(fieldId => {
        return api.del(`/api/person-fields/${fieldId}`, { params: { user_id: userId } })
            .then(() => { invalidatePersonFieldsCache(); return load({ silent: true }); });
    }, [api, userId, load]);

    return {
        fields,
        isServerAdmin,
        loading,
        error,
        refresh: load,
        create,
        update,
        remove,
    };
}

export default usePersonFieldAdmin;
